import { Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import {
  Archive,
  ArchiveRestore,
  Bookmark,
  CalendarDays,
  CloudUpload,
  Copy,
  Filter,
  Loader2,
  Menu,
  MoreHorizontal,
  Pencil,
  Plus,
  Settings,
  Trash,
  Users,
  X,
} from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { useGetAllClassesForCSV } from '~/entities/classes/api/get-all-classes-for-csv';
import { useUpdateClassArchiveStatus } from '~/entities/classes/api/update-class-archive-status';
import { useGetAllClassesWithSearch } from '~entities/classes';
import { useGetTrialBookedCounts } from '~entities/student/api/get-trial-booked-counts';
import { type ClassSearchDetails } from '~entities/classes/modal/class-search';
import { useGetAllSessions } from '~entities/session/api/get-sessions';
import { useGetWaitListStatus } from '~entities/studios/api/get-wait-list-status';
import { useUpdateWaitListStatus } from '~entities/studios/api/update-wait-list-status';
import {
  type ClassListView,
  useClearClassListView,
  useGetClassListView,
  useSaveClassListView,
} from '~entities/studios/api/class-list-view';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { ClassTrialBookingsDialog } from '~features/classes/ui/class-trial-bookings-dialog';
import { CopyClassButton } from '~features/classes/ui/copy-class-button';
import {
  clearClassListColumnPrefs,
  mergeClassListOrder,
  mergeClassListVisibility,
  pinColumnAfter,
  readClassListColumnPrefs,
  writeClassListColumnPrefs,
} from '~features/classes/ui/class-list-column-prefs';
import {
  classListViewFromState,
  classSearchSortFromSorting,
  orderFromClassListView,
  sortingFromClassListView,
  visibilityFromClassListView,
} from '~features/classes/ui/class-list-saved-view';

import { Card, CardContent, CardHeader } from '~/shared/shadcn/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/shared/shadcn/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shared/shadcn/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/shared/shadcn/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/shared/shadcn/ui/tooltip';
import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableFilters,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import {
  DataTableColumnHeader,
  DataTableColumnHeaderWithFilter,
} from '~shared/components/table';
import { InstructorListCell } from '~shared/components/table/instructor-list-cell';
import { selectedClassLocationValue } from '~features/classes/lib/class-list-location-filter';
import { ClassDayFilter } from '~features/classes/ui/class-day-filter';
import {
  ClassFilterChip,
  ClassOptionFilter,
} from '~features/classes/ui/class-option-filter';
import { type Days } from '~shared/constants/days';
import { exportToCSV } from '~shared/helpers/csv';
import {
  extractDateOnly,
  formatAgeBound,
  formatAgeRange,
  formatClassTime,
} from '~shared/helpers/datetime';
import { capitalizeWords } from '~shared/helpers/text-formatting';
import { useFilterPersistence } from '~shared/hooks/useFilterPersistence';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { formatDateForDisplay } from '~shared/utils/date-time';

import { DeleteClassDialog } from './delete-class-dialog';

// Create a column helper
const TrialCountContext = createContext<Record<string, number>>({});

function TrialsBookedCell({
  classId,
  className,
}: {
  classId: string;
  className: string;
}) {
  const counts = useContext(TrialCountContext);
  const count = counts[classId] ?? 0;
  const [open, setOpen] = useState(false);

  return (
    <div className='px-4'>
      <button
        type='button'
        className='underline'
        onClick={() => {
          setOpen(true);
        }}
      >
        {count}
      </button>
      {open ? (
        <ClassTrialBookingsDialog
          classId={classId}
          className={className}
          count={count}
          open={open}
          onOpenChange={setOpen}
        />
      ) : null}
    </div>
  );
}

const columnHelper = createColumnHelper<ClassSearchDetails>();

// Session dropdown lists active sessions only, so a single large page suffices
const ACTIVE_SESSIONS_LIMIT = 200;

const ArchiveClassMenuItem = ({
  classItem,
}: {
  classItem: ClassSearchDetails;
}) => {
  const archiveMutation = useUpdateClassArchiveStatus();

  const handleArchive = async () => {
    try {
      await archiveMutation.mutateAsync({
        id: classItem._id,
        isArchive: !classItem.isArchive,
      });
      toast.success(
        `Class ${classItem.isArchive ? 'unarchived' : 'archived'} successfully`,
      );
    } catch (error) {
      console.error('Archive error:', error);
      toast.error(
        `Failed to ${classItem.isArchive ? 'unarchive' : 'archive'} class`,
      );
    }
  };

  return (
    <DropdownMenuItem
      onClick={handleArchive}
      disabled={archiveMutation.isPending}
    >
      {archiveMutation.isPending ? (
        <>
          <Loader2 className='mr-2 size-4 animate-spin' />
          {classItem.isArchive ? 'Unarchiving...' : 'Archiving...'}
        </>
      ) : classItem.isArchive ? (
        <>
          <ArchiveRestore className='mr-2 size-4' />
          Unarchive
        </>
      ) : (
        <>
          <Archive className='mr-2 size-4' />
          Archive
        </>
      )}
    </DropdownMenuItem>
  );
};


// Define columns

interface ClassListTableProps {
  /** Studio default view, resolved before the first class search request. */
  savedView?: ClassListView | null;
}

export const ClassListTable = ({ savedView = null }: ClassListTableProps) => {
  const navigate = useNavigate();
  const { hasAdminAccess, hasSuperAdminAccess } = useCurrentUserRole();
  const { data: currencyDetails } = useGetCurrency();
  // Use the filter persistence hook
  const { filters, pagination, updateFilter, updatePagination } =
    useFilterPersistence({
      query: '',
      day: 'All',
      tag: 'All',
      room: 'All',
      age: 'All',
      instructor: 'All',
      sessionId: 'All',
      location: 'All',
      showPastClasses: false,
      showArchivedClasses: false,
    });

  // Track which pages have "select all" enabled
  const [pagesWithSelectAll, setPagesWithSelectAll] = useState<Set<number>>(
    new Set(),
  );
  // Store selected class objects by ID to persist across pages
  const [selectedClassesMap, setSelectedClassesMap] = useState<
    Map<string, ClassSearchDetails>
  >(new Map());

  const cols = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        enableHiding: false,
        header: ({ table }) => {
          const currentPageIndex = pagination.pageIndex;
          const isAllSelectedOnPage = pagesWithSelectAll.has(currentPageIndex);

          return (
            <div className='px-4'>
              <Checkbox
                checked={isAllSelectedOnPage}
                onCheckedChange={(value) => {
                  const isChecked = !!value;
                  setPagesWithSelectAll((prev) => {
                    const newSet = new Set(prev);
                    if (isChecked) {
                      newSet.add(currentPageIndex);
                      // Select all rows on current page
                      const currentPageRows = table.getRowModel().rows;
                      setRowSelection((prevSelection) => {
                        const newSelection = { ...prevSelection };
                        currentPageRows.forEach((row) => {
                          newSelection[row.id] = true;
                        });
                        return newSelection;
                      });
                      // Store class objects in the map
                      setSelectedClassesMap((prevMap) => {
                        const newMap = new Map(prevMap);
                        currentPageRows.forEach((row) => {
                          const classItem = row.original;
                          const rowId = classItem._id.toString();
                          newMap.set(rowId, classItem);
                        });
                        return newMap;
                      });
                    } else {
                      newSet.delete(currentPageIndex);
                      // Unselect all rows on current page
                      const currentPageRows = table.getRowModel().rows;
                      setRowSelection((prevSelection) => {
                        const newSelection = { ...prevSelection };
                        currentPageRows.forEach((row) => {
                          const rowId = row.id;
                          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                          delete newSelection[rowId];
                        });
                        return newSelection;
                      });
                      // Remove class objects from the map
                      setSelectedClassesMap((prevMap) => {
                        const newMap = new Map(prevMap);
                        currentPageRows.forEach((row) => {
                          const classItem = row.original;
                          const rowId = classItem._id.toString();
                          newMap.delete(rowId);
                        });
                        return newMap;
                      });
                    }
                    return newSet;
                  });
                }}
                aria-label='Select all'
              />
            </div>
          );
        },
        cell: ({ row }) => {
          return (
            <div className='px-4'>
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => {
                  row.toggleSelected(!!value);
                  // If unchecking a row, remove the page from select all set
                  if (!value) {
                    setPagesWithSelectAll((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(pagination.pageIndex);
                      return newSet;
                    });
                  }
                }}
                aria-label='Select row'
              />
            </div>
          );
        },
      }),
      columnHelper.accessor('title', {
        id: 'title',
        enableHiding: false,
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Class Name'
            disableHiding
          />
        ),
        cell: (info) => (
          <Button variant={'link'} className='text-custom-secondary-1' asChild>
            <Link
              to={`/classes/$classId/enrolled-students`}
              params={{
                classId: info.row.original._id.toString(),
              }}
            >
              {info.getValue()}
            </Link>
          </Button>
        ),
      }),
      columnHelper.accessor('maxSize', {
        id: 'Number of Seats',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Capacity'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('remainingSeats', {
        id: 'Remaining Seats',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Spots remaining'
          />
        ),
        enableSorting: false,
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.display({
        id: 'Trials booked',
        enableHiding: false,
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Trials booked'
            disableHiding
          />
        ),
        enableSorting: false,
        cell: (info) => (
          <TrialsBookedCell
            classId={info.row.original._id.toString()}
            className={info.row.original.title}
          />
        ),
      }),
      columnHelper.accessor('session', {
        id: 'Session',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Session'
            disableHiding
          />
        ),
        cell: (info) => {
          const sessions = info.getValue();
          // Handle array of sessions and display first session name
          return (
            <div className='flex gap-2 px-4'>
              {sessions && sessions.length > 0 ? sessions[0]?.name : ''}
            </div>
          );
        },
      }),
      columnHelper.accessor(
        (v) => {
          return v.days.join(', ');
        },
        {
          id: 'days',
          header: (info) => (
            <DataTableColumnHeader
              className='pl-4 text-black'
              column={info.column}
              title='Days'
              disableHiding
            />
          ),
          cell: (info) => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            const days = info.row.original.days || [];
            return (
              <div className='flex gap-2 px-4'>
                {days.map((v) => {
                  return <Badge key={v}>{v}</Badge>;
                })}
              </div>
            );
          },
        },
      ),
      columnHelper.accessor('location.name', {
        id: 'Location Name',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Location Name'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4'>{capitalizeWords(info.getValue())}</div>
        ),
      }),
      columnHelper.accessor('room.name', {
        id: 'Room',
        header: (info) => (
          <DataTableColumnHeaderWithFilter
            className='pl-4 text-black'
            info={info}
            title='Room'
          />
        ),
        cell: (info) => (
          <div className='px-4'>{capitalizeWords(info.getValue())}</div>
        ),
      }),

      columnHelper.accessor(
        (v) => {
          return formatAgeRange(
            v.startYear,
            v.startMonth,
            v.endYear,
            v.endMonth,
          );
        },
        {
          id: 'Age (Range)',
          header: (info) => (
            <DataTableColumnHeaderWithFilter
              className='pl-4 text-black'
              info={info}
              title='Age (Range)'
            />
          ),
          cell: (info) => {
            return <div className='px-4'>{info.getValue() || ''}</div>;
          },
        },
      ),
      columnHelper.accessor('instructor', {
        id: 'Instructor',
        header: (info) => (
          <DataTableColumnHeader
            className='min-w-[200px] text-center text-black'
            column={info.column}
            title='Instructor'
          />
        ),
        cell: (info) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          const instructors = info.getValue() || [];
          return <InstructorListCell instructors={instructors} />;
        },
      }),

      columnHelper.accessor(
        (v) => {
          const firstAvailability = v.availability?.[0];

          if (!firstAvailability) {
            return '-';
          }

          try {
            const startTime = new Date(firstAvailability.startTime);
            const endTime = new Date(firstAvailability.endTime);

            return `${format(startTime, 'p')} - ${format(endTime, 'p')}`;
          } catch (error) {
            console.error('Error formatting time:', error);
            return '-';
          }
        },
        {
          id: 'Time',
          header: (info) => (
            <DataTableColumnHeaderWithFilter
              className='pl-4 text-black'
              info={info}
              title='Time'
            />
          ),
          cell: (info) => (
            <div className='w-40 px-4'>
              {info.getValue() || ''}
              {/* {`${info.row.original.startTime || ''} - ${info.row.original.endTime || ''}`} */}
            </div>
          ),
        },
      ),
      columnHelper.accessor('startDate', {
        id: 'Start Date',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Start Date'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const extractedDate = value ? extractDateOnly(value) : null;

          if (!extractedDate) {
            return (
              <div className='flex gap-2 px-4'>
                <span>-</span>
              </div>
            );
          }

          // Use the date string directly with the format function
          // This avoids any Date object creation which can introduce timezone issues
          return (
            <div className='flex gap-2 px-4'>
              <span>
                {extractedDate.replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1')}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor('endDate', {
        id: 'End Date',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='End Date'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const extractedDate = value ? extractDateOnly(value) : null;

          if (!extractedDate) {
            return (
              <div className='flex gap-2 px-4'>
                <span>-</span>
              </div>
            );
          }

          // Use the date string directly with the format function
          // This avoids any Date object creation which can introduce timezone issues
          return (
            <div className='flex gap-2 px-4'>
              <span>
                {extractedDate.replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1')}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor('tuitionFee', {
        id: 'Tuition Fee',
        header: (info) => (
          <DataTableColumnHeaderWithFilter
            className='pl-4 text-black'
            info={info}
            title='Tuition Fee'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return value ? (
            <div className='px-4'>
              {currencyDetails?.symbol}
              {value}
            </div>
          ) : (
            <div className='px-4'>-</div>
          );
        },
      }),
      columnHelper.accessor('tags', {
        id: 'Tags',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Tags'
          />
        ),
        cell: (info) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          const tags = info.getValue() || [];
          return (
            <div className='flex gap-2 px-4'>
              {tags.map((v) => {
                return (
                  <Badge key={v._id} className='capitalize'>
                    {v.name ?? ''}
                  </Badge>
                );
              })}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'action',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Action'
          />
        ),
        enableHiding: false,
        cell: (info) => {
          const classItem = info.row.original;
          return (
            <div className='px-4'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={!hasSuperAdminAccess}>
                  <Button
                    variant='ghost'
                    className='size-8 p-0'
                    disabled={!hasSuperAdminAccess}
                  >
                    <MoreHorizontal className='size-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem asChild>
                    <Link
                      to={`/classes/$classId/update`}
                      params={{
                        classId: classItem._id.toString(),
                      }}
                      className='flex items-center'
                    >
                      <Pencil className='mr-2 size-4' />
                      Edit
                    </Link>
                  </DropdownMenuItem>
                  <CopyClassButton classId={classItem._id.toString()}>
                    {({ openDialog, isPending }) => (
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          openDialog();
                        }}
                        disabled={isPending}
                        className='flex items-center'
                      >
                        {isPending ? (
                          <Loader2 className='mr-2 size-4 animate-spin' />
                        ) : (
                          <Copy className='mr-2 size-4' />
                        )}
                        Copy class
                      </DropdownMenuItem>
                    )}
                  </CopyClassButton>
                  <ArchiveClassMenuItem classItem={classItem} />
                  <DropdownMenuItem
                    className='text-red-600'
                    onClick={() => {
                      setSelectedClasses([classItem]);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash className='mr-2 size-4' />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      }),
    ],
    [
      hasSuperAdminAccess,
      currencyDetails,
      pagination.pageIndex,
      pagesWithSelectAll,
    ],
  );

  const initialVisibility = cols.reduce<VisibilityState>((acc, column) => {
    const id = column.id;
    if (id) {
      acc[id] = true;
    }
    return acc;
  }, {});

  const columnIds = cols.flatMap((column) => (column.id ? [column.id] : []));
  const [sorting, setSorting] = useState<SortingState>(() =>
    sortingFromClassListView(savedView),
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () =>
      savedView
        ? visibilityFromClassListView(savedView, initialVisibility)
        : mergeClassListVisibility(
            readClassListColumnPrefs()?.visibility,
            initialVisibility,
          ),
  );
  const [columnOrder, setColumnOrder] = useState<Array<string>>(() =>
    pinColumnAfter(
      savedView
        ? orderFromClassListView(savedView, columnIds)
        : mergeClassListOrder(readClassListColumnPrefs()?.order, columnIds),
      'Trials booked',
      'Remaining Seats',
    ),
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  useEffect(() => {
    setColumnOrder((current) =>
      pinColumnAfter(
        mergeClassListOrder(current, columnIds),
        'Trials booked',
        'Remaining Seats',
      ),
    );
    setColumnVisibility((current) => ({ ...current, 'Trials booked': true }));
  }, [columnIds.join('|')]);

  useEffect(() => {
    writeClassListColumnPrefs({
      visibility: columnVisibility,
      order: columnOrder,
    });
  }, [columnVisibility, columnOrder]);

  const resetColumnPrefs = useCallback(() => {
    clearClassListColumnPrefs();
    setColumnVisibility(initialVisibility);
    setColumnOrder(mergeClassListOrder([], columnIds));
  }, [columnIds, initialVisibility]);

  const { sortBy, sortOrder } = classSearchSortFromSorting(sorting);

  const handleSortingChange = useCallback(
    (updater: React.SetStateAction<SortingState>) => {
      setSorting((prev) => {
        const nextSortedState =
          typeof updater === 'function' ? updater(prev) : updater;
        updatePagination((prevPagination) => ({
          ...prevPagination,
          pageIndex: 0,
        }));
        return nextSortedState;
      });
    },
    [updatePagination],
  );

  const { data: studioView = savedView } = useGetClassListView();
  const saveClassListView = useSaveClassListView();
  const clearClassListView = useClearClassListView();

  const applyStudioView = useCallback(
    (view: ClassListView) => {
      handleSortingChange(sortingFromClassListView(view));
      setColumnVisibility({
        ...visibilityFromClassListView(view, initialVisibility),
        'Trials booked': true,
      });
      setColumnOrder(
        pinColumnAfter(
          orderFromClassListView(view, columnIds),
          'Trials booked',
          'Remaining Seats',
        ),
      );
    },
    [columnIds, handleSortingChange, initialVisibility],
  );

  const handleSaveStudioView = async () => {
    try {
      await saveClassListView.mutateAsync(
        classListViewFromState({
          sorting,
          visibility: columnVisibility,
          order: columnOrder,
        }),
      );
      toast.success('Saved as the studio default view');
    } catch {
      toast.error('Error saving the studio default view');
    }
  };

  const handleClearStudioView = async () => {
    try {
      await clearClassListView.mutateAsync();
      toast.success('Studio default view removed');
    } catch {
      toast.error('Error removing the studio default view');
    }
  };

  const { data: waitListStatus } = useGetWaitListStatus();
  const updateWaitListStatus = useUpdateWaitListStatus();

  const { data: classesData, isFetching } = useGetAllClassesWithSearch({
    name: filters.query,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    sessionId: filters.sessionId !== 'All' ? filters.sessionId : undefined,
    instructor: filters.instructor !== 'All' ? filters.instructor : undefined,
    tags: filters.tag !== 'All' ? filters.tag : undefined,
    days: filters.day !== 'All' ? (filters.day as Days) : undefined,
    room: filters.room !== 'All' ? filters.room : undefined,
    age: filters.age !== 'All' ? filters.age : undefined,
    location: filters.location !== 'All' ? filters.location : undefined,
    studio: true,
    showPastClasses: filters.showPastClasses,
    isArchive: filters.showArchivedClasses,
    enforceStatusFilters: true,
    sortBy,
    sortOrder,
  });

  const data = useMemo(
    () => classesData?.data.classes ?? [],
    [classesData?.data.classes],
  );
  const { data: trialCounts } = useGetTrialBookedCounts(
    data.map((classItem) => classItem._id.toString()),
  );
  const totalCount = classesData?.pagination.totalCount ?? 0;
  const instructors = classesData?.data.instructors ?? [];
  const tags = classesData?.data.tags ?? [];
  const { data: activeSessionsData } = useGetAllSessions({
    page: 1,
    limit: ACTIVE_SESSIONS_LIMIT,
    excludePastSessions: true,
  });
  const sessions = activeSessionsData?.sessions ?? [];
  const rooms = classesData?.data.rooms ?? [];
  const locations = classesData?.data.locations ?? [];
  const roomOptions = useMemo(
    () => rooms.map((room) => ({ value: String(room._id), label: room.name })),
    [rooms],
  );
  const tagOptions = useMemo(
    () => tags.map((tag) => ({ value: tag.name, label: tag.name })),
    [tags],
  );
  const sessionOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const session of [...(classesData?.data.sessions ?? []), ...sessions]) {
      if (session._id && session.name) {
        byId.set(String(session._id), session.name);
      }
    }
    return [...byId.entries()].map(([value, label]) => ({ value, label }));
  }, [classesData?.data.sessions, sessions]);
  const instructorOptions = useMemo(
    () =>
      instructors.map((instructor) => ({
        value: instructor.name,
        label: capitalizeWords(instructor.name),
      })),
    [instructors],
  );
  const locationOptions = useMemo(
    () =>
      locations.map((location) => ({
        value: String(location._id),
        label: capitalizeWords(location.name),
      })),
    [locations],
  );
  const maxAge = classesData?.data.ages?.maxAge ?? 0;
  const minAge = classesData?.data.ages?.minAge ?? 0;
  const ages = Array.from(
    { length: maxAge - minAge + 1 },
    (_, i) => i + minAge,
  );

  // Auto-select rows when navigating to a page that has "select all" enabled
  useEffect(() => {
    if (pagesWithSelectAll.has(pagination.pageIndex) && data.length > 0) {
      setRowSelection((prevSelection) => {
        const newSelection = { ...prevSelection };
        // Select all rows on current page using their IDs
        data.forEach((classItem) => {
          const rowId = classItem._id.toString();
          newSelection[rowId] = true;
        });
        return newSelection;
      });
      // Store class objects in the map
      setSelectedClassesMap((prevMap) => {
        const newMap = new Map(prevMap);
        data.forEach((classItem) => {
          const rowId = classItem._id.toString();
          newMap.set(rowId, classItem);
        });
        return newMap;
      });
    }
  }, [pagination.pageIndex, pagesWithSelectAll, data]);

  // Update selectedClassesMap when individual rows are selected/deselected
  useEffect(() => {
    setSelectedClassesMap((prevMap) => {
      const newMap = new Map(prevMap);
      const selectedIds = Object.keys(rowSelection);

      // Remove classes that are no longer selected
      for (const [id] of newMap) {
        if (!selectedIds.includes(id)) {
          newMap.delete(id);
        }
      }

      // Add classes from current page that are selected
      data.forEach((classItem) => {
        const rowId = classItem._id.toString();
        if (rowSelection[rowId]) {
          newMap.set(rowId, classItem);
        }
      });

      return newMap;
    });
  }, [rowSelection, data]);

  // Clear selection when filters or search change
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    // Check if filters have actually changed (not just initial render)
    const filtersChanged =
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      // Clear all selection state when filters change
      setRowSelection({});
      setPagesWithSelectAll(new Set());
      setSelectedClassesMap(new Map());
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState<
    Array<ClassSearchDetails>
  >([]);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [classActionsOpen, setClassActionsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const closeClassActions = () => {
    setClassActionsOpen(false);
  };

  const getSelectedClassesFromMap = useCallback(() => {
    const selectedIds = Object.keys(rowSelection);
    return Array.from(selectedClassesMap.values()).filter((classItem) =>
      selectedIds.includes(classItem._id.toString()),
    );
  }, [rowSelection, selectedClassesMap]);

  const handleEditSelectedClasses = () => {
    const localSelectedClasses = getSelectedClassesFromMap();
    if (localSelectedClasses.length > 1) {
      toast.error('Please select only one class to edit');
      return;
    }
    setSelectedClasses(localSelectedClasses);
    const selectedClass = localSelectedClasses[0];
    if (selectedClass !== undefined) {
      void navigate({
        to: '/classes/$classId/update',
        params: { classId: selectedClass._id },
      });
    }
  };

  const handleOpenDeleteSelectedClasses = () => {
    setSelectedClasses(getSelectedClassesFromMap());
    setDeleteDialogOpen(true);
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.day !== 'All') count++;
    if (filters.tag !== 'All') count++;
    if (filters.room !== 'All') count++;
    if (filters.age !== 'All') count++;
    if (filters.instructor !== 'All') count++;
    if (filters.sessionId !== 'All') count++;
    if (filters.location !== 'All') count++;
    return count;
  }, [filters]);

  const clearFilters = () => {
    updateFilter('room', 'All');
    updateFilter('tag', 'All');
    updateFilter('sessionId', 'All');
    updateFilter('age', 'All');
    updateFilter('instructor', 'All');
    updateFilter('day', 'All');
    updateFilter('location', 'All');
    updateFilter('showPastClasses', false);
    updateFilter('showArchivedClasses', false);
  };

  const { refetch: getAllClassesForCSV } = useGetAllClassesForCSV({
    enabled: false,
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { data: AllClassesForCSVData } = await getAllClassesForCSV();

      const headers = [
        'Title',
        'Location',
        'Days',
        'Start Time',
        'End Time',
        'Duration (mins)',
        'Registration Start Date',
        'Start Date',
        'End Date',
        'Tuition Fee',
        'Capacity',
        'Tuition Billing Cycle',
        'Registration Fee Amount',
        'Description',
        'Start Age',
        'End Age',
        'Room',
        'Instructor',
        'Tags',
        'Group',
        'Policy',
      ];
      const formattedData =
        AllClassesForCSVData?.map((classData) => ({
          Title: capitalizeWords(classData.title),
          Location: capitalizeWords(classData.location?.fieldName ?? ''),
          Days: classData.days.join(', '),
          'Start Time': formatClassTime(classData.startTime) ?? '-',
          'End Time': formatClassTime(classData.endTime) ?? '-',
          'Duration (mins)': classData.duration,
          'Registration Start Date': classData.registrationStartDate
            ? formatDateForDisplay(classData.registrationStartDate)
            : '-',
          'Start Date': classData.startDate
            ? formatDateForDisplay(classData.startDate)
            : '-',
          'End Date': classData.endDate
            ? formatDateForDisplay(classData.endDate)
            : '-',
          'Tuition Fee': classData.tuitionFee,
          Capacity: classData.maxSize,
          'Tuition Billing Cycle': capitalizeWords(
            classData.tuitionBillingCycle,
          ),
          'Registration Fee Amount': classData.registrationFeeAmount,
          Description:
            classData.description.length > 0
              ? classData.description.charAt(0).toUpperCase() +
                classData.description.slice(1)
              : '',
          'Start Age': formatAgeBound(
            classData.startYear,
            classData.startMonth,
          ),
          'End Age': formatAgeBound(classData.endYear, classData.endMonth),
          Room: capitalizeWords(classData.room),
          Instructor: classData.instructor.map(capitalizeWords).join(', '),
          Tags: classData.tags.map(capitalizeWords).join(', '),
          Group: classData.group.map(capitalizeWords).join(', '),
          Policy: classData.policyGroup.map(capitalizeWords).join(', '),
        })) ?? [];

      exportToCSV(
        formattedData,
        headers,
        `classes-export-${new Date().toISOString()}.csv`,
      );
      toast.success('Classes exported successfully');
    } catch (error) {
      console.error('Error exporting classes:', error);
      toast.error('Error exporting classes');
    } finally {
      setIsExporting(false);
    }
  };

  const handleUpdateWaitListStatus = async (status: boolean) => {
    try {
      await updateWaitListStatus.mutateAsync({
        waitlistEnabled: status,
      });
      toast.success(
        `Wait list ${status ? 'enabled' : 'disabled'} successfully`,
      );
    } catch (error) {
      console.error('Error updating wait list status:', error);
      toast.error(`Error ${status ? 'enabling' : 'disabling'} wait list`);
    }
  };

  const handleUpdateHideSpotsRemaining = async (status: boolean) => {
    try {
      await updateWaitListStatus.mutateAsync({
        hideSpotsRemaining: status,
      });
      toast.success(
        `Spots remaining ${status ? 'hidden' : 'shown'} successfully`,
      );
    } catch (error) {
      console.error('Error updating spots remaining visibility:', error);
      toast.error(`Error ${status ? 'hiding' : 'showing'} spots remaining`);
    }
  };

  const handleUpdateClassPaymentDetails = async (status: boolean) => {
    try {
      await updateWaitListStatus.mutateAsync({
        classPaymentDetails: status,
      });
      toast.success(
        `Class payment details ${status ? 'enabled' : 'disabled'} successfully`,
      );
    } catch (error) {
      console.error('Error updating class payment details:', error);
      toast.error(
        `Error ${status ? 'enabling' : 'disabling'} class payment details`,
      );
    }
  };

  const handleUpdateAutoFilterClassesByStudentAge = async (status: boolean) => {
    try {
      await updateWaitListStatus.mutateAsync({
        autoFilterClassesByStudentAge: status,
      });
      toast.success(
        `Auto-filter Find Classes by student age ${status ? 'enabled' : 'disabled'} successfully`,
      );
    } catch (error) {
      console.error(
        'Error updating auto-filter Find Classes by student age:',
        error,
      );
      toast.error(
        `Error ${status ? 'enabling' : 'disabling'} auto-filter Find Classes by student age`,
      );
    }
  };

  const selectedCount = Object.keys(rowSelection).length;
  const selectionActions =
    hasSuperAdminAccess && selectedCount > 0 ? (
      <>
        <Button
          variant='outline'
          className='shrink-0'
          title={`Edit Selected (${selectedCount})`}
          aria-label={`Edit Selected (${selectedCount})`}
          onClick={handleEditSelectedClasses}
        >
          <Pencil />
          <span className='sr-only md:not-sr-only md:whitespace-nowrap'>
            Edit Selected ({selectedCount})
          </span>
        </Button>
        <Button
          variant='destructive'
          className='shrink-0'
          title={`Delete Selected (${selectedCount})`}
          aria-label={`Delete Selected (${selectedCount})`}
          onClick={handleOpenDeleteSelectedClasses}
        >
          <Trash />
          <span className='whitespace-nowrap md:hidden'>
            Delete? ({selectedCount})
          </span>
          <span className='hidden whitespace-nowrap md:inline'>
            Delete Selected ({selectedCount})
          </span>
        </Button>
      </>
    ) : null;

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end'>
          <div className='hidden flex-wrap gap-2 md:flex'>
            <Button
              className='flex items-center bg-custom-secondary-1 text-white hover:bg-custom-secondary-1'
              asChild
            >
              <Link to='/classes/trial-students'>
                <Users />
                <span className='hidden md:inline'>Trial Students</span>
              </Link>
            </Button>
            <Button
              className='flex items-center bg-custom-secondary-1 text-white hover:bg-custom-secondary-1'
              asChild
            >
              <Link to='/classes/private-lessons'>
                <CalendarDays />
                <span className='hidden md:inline'>Private Lessons</span>
              </Link>
            </Button>
            {hasAdminAccess && (
              <Button
                className='flex items-center bg-custom-secondary-1 text-white hover:bg-custom-secondary-1'
                asChild
              >
                <Link to='/classes/new'>
                  <Plus />
                  <span className='hidden md:inline'>Add Classes</span>
                </Link>
              </Button>
            )}
            <Button asChild className='hidden md:flex'>
              <Link to='/class-calendar'>Class Calendar</Link>
            </Button>
            <Button asChild variant='outline' className='hidden md:flex'>
              <Link to='/room-overview'>Room Overview</Link>
            </Button>
            {hasAdminAccess && (
              <Button
                variant={'outline'}
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 className='animate-spin' />
                ) : (
                  <CloudUpload />
                )}
                <span className='hidden md:inline'>
                  {isExporting ? 'Exporting...' : 'Export'}
                </span>
              </Button>
            )}
            {(hasAdminAccess || studioView) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline' aria-label='Default view'>
                    <Bookmark />
                    <span className='hidden md:inline'>Default View</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  {hasAdminAccess && (
                    <DropdownMenuItem
                      onClick={() => void handleSaveStudioView()}
                      disabled={saveClassListView.isPending}
                    >
                      Save current view as studio default
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => {
                      if (studioView) applyStudioView(studioView);
                    }}
                    disabled={!studioView}
                  >
                    Reset to studio default
                  </DropdownMenuItem>
                  {hasAdminAccess && studioView && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => void handleClearStudioView()}
                        disabled={clearClassListView.isPending}
                      >
                        Remove studio default
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {hasSuperAdminAccess && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline'>
                    <Settings />
                    <span className='hidden md:inline'>Settings</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={() =>
                      handleUpdateWaitListStatus(
                        !waitListStatus?.waitlistEnabled,
                      )
                    }
                    disabled={updateWaitListStatus.isPending}
                  >
                    {waitListStatus?.waitlistEnabled ? 'Disable' : 'Enable'}{' '}
                    Wait List
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      handleUpdateHideSpotsRemaining(
                        !waitListStatus?.hideSpotsRemaining,
                      )
                    }
                    disabled={updateWaitListStatus.isPending}
                  >
                    {waitListStatus?.hideSpotsRemaining ? 'Show' : 'Hide'}{' '}
                    Remaining Spots
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      handleUpdateClassPaymentDetails(
                        !waitListStatus?.classPaymentDetails,
                      )
                    }
                    disabled={updateWaitListStatus.isPending}
                  >
                    {waitListStatus?.classPaymentDetails ? 'Disable' : 'Enable'}{' '}
                    Class Payment Details
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    title='When enabled, Parent Portal pre-filters Find Classes to age-eligible offerings for the student being enrolled. Parents can still clear or adjust the age filters manually.'
                    onClick={() =>
                      handleUpdateAutoFilterClassesByStudentAge(
                        !waitListStatus?.autoFilterClassesByStudentAge,
                      )
                    }
                    disabled={updateWaitListStatus.isPending}
                  >
                    {waitListStatus?.autoFilterClassesByStudentAge
                      ? 'Disable'
                      : 'Enable'}{' '}
                    Auto-filter Find Classes by student age
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <EnrollioTableFilters className='mb-6 md:flex-col md:items-stretch'>
          <div className='flex flex-col gap-4'>
            <EnrollioTableSearch
              value={filters.query}
              onChange={(value) => {
                updateFilter('query', value);
              }}
              placeholder='Search classes...'
            />
            {hasSuperAdminAccess && selectedCount > 0 ? (
              <div className='flex w-full min-w-0 gap-2 md:hidden'>
                <Button
                  variant='outline'
                  size='sm'
                  className='h-auto min-w-0 flex-1 gap-1.5 whitespace-normal px-2 py-2 text-xs leading-snug'
                  onClick={handleEditSelectedClasses}
                >
                  <Pencil className='size-4 shrink-0' />
                  Edit Selected ({selectedCount})
                </Button>
                <Button
                  variant='destructive'
                  size='sm'
                  className='h-auto min-w-0 flex-1 gap-1.5 whitespace-normal px-2 py-2 text-xs leading-snug'
                  onClick={handleOpenDeleteSelectedClasses}
                >
                  <Trash className='size-4 shrink-0' />
                  Delete Selected ({selectedCount})
                </Button>
              </div>
            ) : null}
            <Sheet open={classActionsOpen} onOpenChange={setClassActionsOpen}>
              <SheetTrigger asChild>
                <Button variant='outline' className='relative w-full md:hidden'>
                  <Menu className='mr-2 size-4' />
                  Class actions
                </Button>
              </SheetTrigger>
              <SheetContent side='right' className='w-[300px] overflow-y-auto'>
                <SheetHeader>
                  <SheetTitle>Class actions</SheetTitle>
                  <SheetDescription>
                    Open class tools, export, change settings, or go to related
                    pages.
                  </SheetDescription>
                </SheetHeader>
                <div className='mt-6 flex flex-col gap-3'>
                  <Button
                    className='h-auto w-full justify-start whitespace-normal bg-custom-secondary-1 py-2 text-left text-white hover:bg-custom-secondary-1'
                    asChild
                  >
                    <Link
                      to='/classes/trial-students'
                      onClick={closeClassActions}
                    >
                      <Users className='mr-2 size-4 shrink-0' />
                      Trial Students
                    </Link>
                  </Button>
                  <Button
                    className='h-auto w-full justify-start whitespace-normal bg-custom-secondary-1 py-2 text-left text-white hover:bg-custom-secondary-1'
                    asChild
                  >
                    <Link
                      to='/classes/private-lessons'
                      onClick={closeClassActions}
                    >
                      <CalendarDays className='mr-2 size-4 shrink-0' />
                      Private Lessons
                    </Link>
                  </Button>
                  {hasAdminAccess ? (
                    <Button
                      className='h-auto w-full justify-start whitespace-normal bg-custom-secondary-1 py-2 text-left text-white hover:bg-custom-secondary-1'
                      asChild
                    >
                      <Link to='/classes/new' onClick={closeClassActions}>
                        <Plus className='mr-2 size-4 shrink-0' />
                        Add Classes
                      </Link>
                    </Button>
                  ) : null}
                  <Button
                    variant='outline'
                    className='h-auto w-full justify-start whitespace-normal py-2 text-left'
                    asChild
                  >
                    <Link to='/class-calendar' onClick={closeClassActions}>
                      Class Calendar
                    </Link>
                  </Button>
                  <Button
                    variant='outline'
                    className='h-auto w-full justify-start whitespace-normal py-2 text-left'
                    asChild
                  >
                    <Link to='/room-overview' onClick={closeClassActions}>
                      Room Overview
                    </Link>
                  </Button>
                  {hasAdminAccess ? (
                    <Button
                      variant='outline'
                      className='h-auto w-full justify-start whitespace-normal py-2 text-left'
                      disabled={isExporting}
                      onClick={() => {
                        closeClassActions();
                        void handleExport();
                      }}
                    >
                      {isExporting ? (
                        <Loader2 className='mr-2 size-4 shrink-0 animate-spin' />
                      ) : (
                        <CloudUpload className='mr-2 size-4 shrink-0' />
                      )}
                      {isExporting ? 'Exporting...' : 'Export'}
                    </Button>
                  ) : null}
                  {hasSuperAdminAccess ? (
                    <>
                      <Button
                        variant='outline'
                        className='h-auto w-full justify-start whitespace-normal py-2 text-left'
                        disabled={updateWaitListStatus.isPending}
                        onClick={() => {
                          closeClassActions();
                          void handleUpdateWaitListStatus(
                            !waitListStatus?.waitlistEnabled,
                          );
                        }}
                      >
                        {waitListStatus?.waitlistEnabled ? 'Disable' : 'Enable'}{' '}
                        Wait List
                      </Button>
                      <Button
                        variant='outline'
                        className='h-auto w-full justify-start whitespace-normal py-2 text-left'
                        disabled={updateWaitListStatus.isPending}
                        onClick={() => {
                          closeClassActions();
                          void handleUpdateHideSpotsRemaining(
                            !waitListStatus?.hideSpotsRemaining,
                          );
                        }}
                      >
                        {waitListStatus?.hideSpotsRemaining ? 'Show' : 'Hide'}{' '}
                        Remaining Spots
                      </Button>
                      <Button
                        variant='outline'
                        className='h-auto w-full justify-start whitespace-normal py-2 text-left'
                        disabled={updateWaitListStatus.isPending}
                        onClick={() => {
                          closeClassActions();
                          void handleUpdateClassPaymentDetails(
                            !waitListStatus?.classPaymentDetails,
                          );
                        }}
                      >
                        {waitListStatus?.classPaymentDetails
                          ? 'Disable'
                          : 'Enable'}{' '}
                        Class Payment Details
                      </Button>
                      <Button
                        variant='outline'
                        className='h-auto w-full justify-start whitespace-normal py-2 text-left'
                        disabled={updateWaitListStatus.isPending}
                        onClick={() => {
                          closeClassActions();
                          void handleUpdateAutoFilterClassesByStudentAge(
                            !waitListStatus?.autoFilterClassesByStudentAge,
                          );
                        }}
                      >
                        {waitListStatus?.autoFilterClassesByStudentAge
                          ? 'Disable'
                          : 'Enable'}{' '}
                        Auto-filter Find Classes by student age
                      </Button>
                    </>
                  ) : null}
                </div>
              </SheetContent>
            </Sheet>
            {/* Mobile Filters Button */}
            <div className='flex md:hidden'>
              <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant='outline' className='relative w-full'>
                    <Filter className='mr-2 size-4' />
                    Filters
                    {activeFilterCount > 0 && (
                      <Badge className='ml-2' variant='secondary'>
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side='right'
                  className='w-[300px] overflow-y-auto'
                >
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                    <SheetDescription>
                      Filter classes by various criteria
                    </SheetDescription>
                  </SheetHeader>
                  <div className='mt-6 flex flex-col gap-4'>
                    {/* Filter Room */}
                    <div>
                      <label className='mb-2 block text-sm font-medium'>
                        Room
                      </label>
                      <ClassOptionFilter
                        className='w-full justify-start'
                        value={filters.room}
                        allLabel='All Rooms'
                        options={roomOptions}
                        onChange={(value) => {
                          updateFilter('room', value);
                        }}
                      />
                    </div>
                    <div>
                      <label className='mb-2 block text-sm font-medium'>
                        Tags
                      </label>
                      <ClassOptionFilter
                        className='w-full justify-start'
                        value={filters.tag}
                        allLabel='All Tags'
                        options={tagOptions}
                        onChange={(value) => {
                          updateFilter('tag', value);
                        }}
                      />
                    </div>
                    <div>
                      <label className='mb-2 block text-sm font-medium'>
                        Session
                      </label>
                      <ClassOptionFilter
                        className='w-full justify-start'
                        value={filters.sessionId}
                        allLabel='All Sessions'
                        options={sessionOptions}
                        onChange={(value) => {
                          updateFilter('sessionId', value);
                        }}
                      />
                    </div>
                    {/* Filter Age */}
                    <div>
                      <label className='mb-2 block text-sm font-medium'>
                        Age Group
                      </label>
                      <Select
                        value={filters.age}
                        onValueChange={(value) => {
                          updateFilter('age', value);
                        }}
                      >
                        <SelectTrigger className='w-full'>
                          <SelectValue placeholder='Age Group' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='All'>All Ages</SelectItem>
                          {ages.map((age) => (
                            <SelectItem key={age} value={age.toString()}>
                              {age}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Filter Instructor */}
                    <div>
                      <label className='mb-2 block text-sm font-medium'>
                        Instructor
                      </label>
                      <ClassOptionFilter
                        className='w-full justify-start'
                        value={filters.instructor}
                        allLabel='All Instructors'
                        options={instructorOptions}
                        onChange={(value) => {
                          updateFilter('instructor', value);
                        }}
                      />
                    </div>
                    {/* Filter Day */}
                    <div>
                      <label className='mb-2 block text-sm font-medium'>
                        Day
                      </label>
                      <ClassDayFilter
                        className='w-full justify-start'
                        value={filters.day}
                        onChange={(value) => {
                          updateFilter('day', value);
                        }}
                      />
                    </div>
                    {/* Filter Location */}
                    <div>
                      <label className='mb-2 block text-sm font-medium'>
                        Location
                      </label>
                      <ClassOptionFilter
                        className='w-full justify-start'
                        value={selectedClassLocationValue(
                          filters.location,
                          locations,
                        )}
                        allLabel='All Locations'
                        options={locationOptions}
                        onChange={(value) => {
                          updateFilter('location', value);
                        }}
                      />
                    </div>
                    {/* Show Past Classes Toggle */}
                    <div className='flex items-center space-x-2'>
                      <Checkbox
                        id='showPastClassesMobile'
                        checked={filters.showPastClasses as boolean}
                        onCheckedChange={(checked) => {
                          updateFilter('showPastClasses', checked === true);
                        }}
                      />
                      <label
                        htmlFor='showPastClassesMobile'
                        className='cursor-pointer text-sm font-medium'
                      >
                        Show Past Classes
                      </label>
                    </div>
                    {/* Show Archived Classes Toggle */}
                    <div className='flex items-center space-x-2'>
                      <Checkbox
                        id='showArchivedClassesMobile'
                        checked={filters.showArchivedClasses as boolean}
                        onCheckedChange={(checked) => {
                          updateFilter('showArchivedClasses', checked === true);
                        }}
                      />
                      <label
                        htmlFor='showArchivedClassesMobile'
                        className='cursor-pointer text-sm font-medium'
                      >
                        Show Archived Classes
                      </label>
                    </div>
                    {activeFilterCount > 0 && (
                      <Button
                        type='button'
                        variant='outline'
                        onClick={clearFilters}
                      >
                        Clear all
                      </Button>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
            {/* Desktop Filters */}
            <div className='hidden flex-wrap items-center gap-2 md:flex'>
              <ClassOptionFilter
                className='w-[160px] justify-start font-normal capitalize'
                value={filters.room}
                allLabel='All Rooms'
                options={roomOptions}
                onChange={(value) => {
                  updateFilter('room', value);
                }}
              />
              <ClassOptionFilter
                className='w-[160px] justify-start font-normal capitalize'
                value={filters.tag}
                allLabel='All Tags'
                options={tagOptions}
                onChange={(value) => {
                  updateFilter('tag', value);
                }}
              />
              <ClassOptionFilter
                className='w-[160px] justify-start font-normal capitalize'
                value={filters.sessionId}
                allLabel='All Sessions'
                options={sessionOptions}
                onChange={(value) => {
                  updateFilter('sessionId', value);
                }}
              />
              {/* Filter Age */}
              <Select
                value={filters.age}
                onValueChange={(value) => {
                  updateFilter('age', value);
                }}
              >
                <SelectTrigger className='w-[160px]'>
                  <SelectValue placeholder='Age Group' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='All'>All Ages</SelectItem>
                  {ages.map((age) => (
                    <SelectItem key={age} value={age.toString()}>
                      {age}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Filter Instructor */}
              <ClassOptionFilter
                className='w-[160px] justify-start font-normal'
                value={filters.instructor}
                allLabel='All Instructors'
                options={instructorOptions}
                onChange={(value) => {
                  updateFilter('instructor', value);
                }}
              />
              {/* Filter Day */}
              <ClassDayFilter
                className='w-[160px] justify-start font-normal'
                value={filters.day}
                onChange={(value) => {
                  updateFilter('day', value);
                }}
              />
              {/* Filter Location */}
              <ClassOptionFilter
                className='w-[160px] justify-start font-normal'
                value={selectedClassLocationValue(filters.location, locations)}
                allLabel='All Locations'
                options={locationOptions}
                onChange={(value) => {
                  updateFilter('location', value);
                }}
              />
              {/* Show Past Classes Checkbox */}
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='showPastClassesDesktop'
                  checked={filters.showPastClasses as boolean}
                  onCheckedChange={(checked) => {
                    updateFilter('showPastClasses', checked === true);
                  }}
                />
                <label
                  htmlFor='showPastClassesDesktop'
                  className='cursor-pointer whitespace-nowrap text-sm font-medium'
                >
                  Past Classes
                </label>
              </div>
              {/* Show Archived Classes Checkbox */}
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='showArchivedClassesDesktop'
                  checked={filters.showArchivedClasses as boolean}
                  onCheckedChange={(checked) => {
                    updateFilter('showArchivedClasses', checked === true);
                  }}
                />
                <label
                  htmlFor='showArchivedClassesDesktop'
                  className='cursor-pointer whitespace-nowrap text-sm font-medium'
                >
                  Archived Classes
                </label>
              </div>
            </div>
          </div>
          {/* Active Filter Chips */}
          {activeFilterCount > 0 && (
            <div className='mt-4 flex flex-wrap items-center gap-2'>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={clearFilters}
              >
                Clear all
              </Button>
              <ClassFilterChip
                label='Room'
                value={filters.room}
                options={roomOptions}
                onClear={() => {
                  updateFilter('room', 'All');
                }}
              />
              <ClassFilterChip
                label='Tag'
                value={filters.tag}
                options={tagOptions}
                onClear={() => {
                  updateFilter('tag', 'All');
                }}
              />
              <ClassFilterChip
                label='Session'
                value={filters.sessionId}
                options={sessionOptions}
                onClear={() => {
                  updateFilter('sessionId', 'All');
                }}
              />
              {filters.age !== 'All' && (
                <Badge variant='secondary' className='gap-1'>
                  Age: {filters.age}
                  <button
                    type='button'
                    onClick={() => {
                      updateFilter('age', 'All');
                    }}
                    className='ml-1 rounded-full hover:bg-secondary-foreground/20'
                  >
                    <X className='size-3' />
                  </button>
                </Badge>
              )}
              <ClassFilterChip
                label='Instructor'
                value={filters.instructor}
                options={instructorOptions}
                onClear={() => {
                  updateFilter('instructor', 'All');
                }}
              />
              <ClassFilterChip
                label='Day'
                value={filters.day}
                options={[]}
                onClear={() => {
                  updateFilter('day', 'All');
                }}
              />
              <ClassFilterChip
                label='Location'
                value={filters.location}
                options={locationOptions}
                onClear={() => {
                  updateFilter('location', 'All');
                }}
              />
            </div>
          )}
        </EnrollioTableFilters>

        <TrialCountContext.Provider value={trialCounts?.byClass ?? {}}>
        <div>
          <EnrollioTable
            ariaLabel='Classes'
            totalLabel='Classes'
            toolbar={(table) => (
              <>
                <div className='hidden items-center gap-2 md:flex'>
                  {selectionActions}
                </div>
                <EnrollioTableColumnToggle
                  table={table}
                  enableReorder
                  onReset={resetColumnPrefs}
                />
              </>
            )}
            columnFilters={columnFilters}
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
            columns={cols}
            data={data}
            rowCount={totalCount}
            enableRowSelection={true}
            isUpdating={isFetching}
            onColumnFiltersChange={setColumnFilters}
            onColumnVisibilityChange={setColumnVisibility}
            onColumnOrderChange={setColumnOrder}
            onRowSelectionChange={setRowSelection}
            onSortingChange={handleSortingChange}
            rowSelection={rowSelection}
            sorting={sorting}
            pagination={pagination}
            onPaginationChange={updatePagination}
            manualPagination
            manualSorting
            getRowId={(row) => row._id.toString()}
          />
        </div>
        </TrialCountContext.Provider>
      </CardContent>
      {deleteDialogOpen && (
        <DeleteClassDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          selectedClasses={selectedClasses}
          onClose={() => {
            setDeleteDialogOpen(false);
            setSelectedClasses([]);
            setRowSelection({});
            setSelectedClassesMap(new Map());
            setPagesWithSelectAll(new Set());
          }}
          isBulkDelete={selectedClasses.length > 1}
        />
      )}
    </Card>
  );
};
